import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { priceInfo as getPriceInfo } from '@/utils/price';
import { getCurrentScrollPercentage, getViewArea } from '@/utils/scroll';
import { env } from '@/utils/env';
import md5 from 'crypto-js/md5';
import { useCallback, useEffect, useRef } from 'react';

const PAGE_NAME = '202605Ulike联名活动页';
const MARKET_ACTIVITY_NAME = '202605Ulike联名活动页';
const MARKET_ACTIVITY_ID = md5('202605Ulike联名活动页').toString();

export function useZeeloolXUlikeTracking() {
  const { trackEvent } = useReporting();
  const { getFromRouterInfo } = useRouteInfo();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);

  const enterTimer = useRef<number>(Date.now());
  const lastScrollState = useRef({ viewAdvance: 0, viewArea: 1 });

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPage_View
   * @EventDesc Ulike联名活动页页面浏览
   * @EventParams {
   *   page_name: 页面名称,
   *   refer_page_name: 前向页面名称,
   *   refer_url_path: 前向页面路径,
   * }
   */
  const trackPageView = useCallback(() => {
    let { refer_page_name, refer_url_path } = getFromRouterInfo();
    enterTimer.current = Date.now();

    // 如果从路由信息中取不到值，从URL查询参数中获取
    if (!refer_page_name || !refer_url_path) {
      if (typeof window !== 'undefined') {
        try {
          const urlParams = new URLSearchParams(window.location.search);
          const paramReferPageName = urlParams.get('refer_page_name');
          const paramReferUrlPath = urlParams.get('refer_url_path');

          if (paramReferPageName) {
            refer_page_name = paramReferPageName;
          }
          if (paramReferUrlPath) {
            refer_url_path = paramReferUrlPath;
          }

          // 如果查询参数也没有，使用默认值
          if (!refer_page_name) {
            refer_page_name = '';
          }
          if (!refer_url_path) {
            refer_url_path = '';
          }
        } catch (error) {
          logger.error('Failed to get URL params:', error);
          refer_page_name = refer_page_name || '';
          refer_url_path = refer_url_path || '';
        }
      }
    }

    try {
      logger.log('trackPageView', refer_page_name, refer_url_path);
      trackEvent(
        {
          eventName: 'CampaignPage_View',
          properties: {
            page_name: PAGE_NAME,
            refer_page_name,
            refer_url_path,
            market_activity_id: MARKET_ACTIVITY_ID,
            market_activity_name: MARKET_ACTIVITY_NAME,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('Ulike page view tracking error:', error);
    }
  }, [trackEvent]);

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPage_Leave
   * @EventDesc Ulike联名活动页页面离开
   * @EventParams {
   *   page_name: 页面名称,
   *   view_advance: 浏览进度,
   *   view_duration: 浏览时长,
   *   view_area: 浏览区域,
   * }
   */
  const trackPageLeave = useCallback(() => {
    const { viewAdvance, viewArea } = lastScrollState.current;

    try {
      logger.log('trackPageLeave', viewAdvance, viewArea);
      trackEvent(
        {
          eventName: 'CampaignPage_Leave',
          properties: {
            page_name: PAGE_NAME,
            view_advance: viewAdvance,
            view_duration: Math.ceil((Date.now() - enterTimer.current) / 1000),
            view_area: viewArea,
            market_activity_id: MARKET_ACTIVITY_ID,
            market_activity_name: MARKET_ACTIVITY_NAME,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('Ulike page leave tracking error:', error);
    }
  }, [trackEvent]);

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageProduct_Exp
   * @EventDesc Ulike联名活动页商品曝光
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   pits_index: 坑位排序,
   *   product_sku: 商品sku,
   *   business_sku: 平台sku,
   *   shop_sku: 站点商品sku,
   *   product_id: 商品id,
   *   product_name: 商品名称,
   *   product_price: 商品原价,
   *   current_product_price: 商品现价,
   *   base_current_product_price: 基础货币现价,
   *   dollar_exchange_rate: 美元汇率,
   *   currency: 货币,
   * }
   */
  const trackProductExp = useCallback(
    (item: any, index: number, moduleName: string) => {
      try {
        const nowPriceInfo = getPriceInfo(userInfo, item, currency);
        const basePriceInfo = getPriceInfo(userInfo, item, env.NEXT_PUBLIC_DEFAULT_CURRENCY);

        trackEvent(
          {
            eventName: 'CampaignPageProduct_Exp',
            properties: {
              page_name: PAGE_NAME,
              module_name: moduleName,
              pits_index: index,
              product_sku: item.sku,
              business_sku: item.business_sku,
              shop_sku: item.shop_sku,
              product_id: `${item.id}`,
              product_name: item.short_name || item.name,
              product_price: parseFloat(nowPriceInfo.price || '0'),
              current_product_price: parseFloat(nowPriceInfo.sell_price || '0'),
              base_current_product_price: parseFloat(basePriceInfo.sell_price || '0'),
              dollar_exchange_rate: parseFloat(currency?.exchange_rate || '0'),
              currency: currency?.name || '',
              market_activity_id: MARKET_ACTIVITY_ID,
              market_activity_name: MARKET_ACTIVITY_NAME,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Ulike product exp tracking error:', error);
      }
    },
    [trackEvent, currency, userInfo],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageProduct_Click
   * @EventDesc Ulike联名活动页商品点击
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   pits_index: 坑位排序,
   *   product_sku: 商品sku,
   *   business_sku: 平台sku,
   *   shop_sku: 站点商品sku,
   *   product_id: 商品id,
   *   product_name: 商品名称,
   *   product_price: 商品原价,
   *   current_product_price: 商品现价,
   *   base_current_product_price: 基础货币现价,
   *   dollar_exchange_rate: 美元汇率,
   *   currency: 货币,
   * }
   */
  const trackProductClick = useCallback(
    (item: any, index: number, moduleName: string) => {
      try {
        const nowPriceInfo = getPriceInfo(userInfo, item, currency);
        const basePriceInfo = getPriceInfo(userInfo, item, env.NEXT_PUBLIC_DEFAULT_CURRENCY);

        trackEvent(
          {
            eventName: 'CampaignPageProduct_Click',
            properties: {
              page_name: PAGE_NAME,
              module_name: moduleName,
              pits_index: index,
              product_sku: item.sku,
              business_sku: item.business_sku,
              shop_sku: item.shop_sku,
              product_id: `${item.id}`,
              product_name: item.short_name || item.name,
              product_price: parseFloat(nowPriceInfo.price || '0'),
              current_product_price: parseFloat(nowPriceInfo.sell_price || '0'),
              base_current_product_price: parseFloat(basePriceInfo.sell_price || '0'),
              dollar_exchange_rate: parseFloat(currency?.exchange_rate || '0'),
              currency: currency?.name || '',
              market_activity_id: MARKET_ACTIVITY_ID,
              market_activity_name: MARKET_ACTIVITY_NAME,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Ulike product click tracking error:', error);
      }
    },
    [trackEvent, currency, userInfo],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageAD_Exp
   * @EventDesc Ulike联名活动页AD曝光
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   pits_index: 坑位排序,
   *   pits_jump_type: 坑位内容的跳转类型,
   *   pits_jump_content: 坑位内容跳转的内容,
   * }
   */
  const trackADExp = useCallback(
    (moduleName: string, pitsIndex: number, pitsJumpType?: string, pitsJumpContent?: string) => {
      try {
        const properties: Record<string, unknown> = {
          page_name: PAGE_NAME,
          module_name: moduleName,
          pits_index: pitsIndex,
          market_activity_id: MARKET_ACTIVITY_ID,
          market_activity_name: MARKET_ACTIVITY_NAME,
        };

        if (pitsJumpType) {
          properties.pits_jump_type = pitsJumpType;
        }
        if (pitsJumpContent) {
          properties.pits_jump_content = pitsJumpContent;
        }

        trackEvent(
          {
            eventName: 'CampaignPageAD_Exp',
            properties,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Ulike AD exp tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CampaignPageAD_Click
   * @EventDesc Ulike联名活动页AD点击
   * @EventParams {
   *   page_name: 页面名称,
   *   module_name: 模块名称,
   *   pits_index: 坑位排序,
   *   pits_jump_type: 坑位内容的跳转类型,
   *   pits_jump_content: 坑位内容跳转的内容,
   * }
   */
  const trackADClick = useCallback(
    (moduleName: string, pitsIndex: number, pitsJumpType?: string, pitsJumpContent?: string) => {
      try {
        const properties: Record<string, unknown> = {
          page_name: PAGE_NAME,
          module_name: moduleName,
          pits_index: pitsIndex,
          market_activity_id: MARKET_ACTIVITY_ID,
          market_activity_name: MARKET_ACTIVITY_NAME,
        };

        if (pitsJumpType) {
          properties.pits_jump_type = pitsJumpType;
        }
        if (pitsJumpContent) {
          properties.pits_jump_content = pitsJumpContent;
        }

        trackEvent(
          {
            eventName: 'CampaignPageAD_Click',
            properties,
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Ulike AD click tracking error:', error);
      }
    },
    [trackEvent],
  );

  useEffect(() => {
    const updateScrollState = () => {
      try {
        const viewAdvance = getCurrentScrollPercentage();
        const viewArea = getViewArea();
        lastScrollState.current = { viewAdvance, viewArea };
      } catch (error) {
        logger.error('Error updating scroll state:', error);
      }
    };

    updateScrollState();

    const handleScroll = () => {
      updateScrollState();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return {
    trackPageView,
    trackPageLeave,
    trackProductExp,
    trackProductClick,
    trackADExp,
    trackADClick,
  };
}
